import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { BACKEND_URL, apiError, inr } from "../../lib/api";
import { PageHeader, Card, Loading, Empty, StatusBadge, btnPrimary } from "../../components/admin/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import {
  Plus, Eye, Pencil, Copy, Share2, QrCode, Pause, Play, Archive, Link2, MoreVertical, MessageCircle, Mail, Trash2, Loader2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

export default function PagesList() {
  const [pages, setPages] = useState(null);
  const [sharePage, setSharePage] = useState(null);
  const [menuId, setMenuId] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/pages").then((r) => setPages(r.data)).catch(() => setPages([]));
  useEffect(() => { load(); }, []);

  const [delTarget, setDelTarget] = useState(null);
  const [impact, setImpact] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const openDelete = async (p) => {
    setMenuId(null); setDelTarget(p); setImpact(null); setConfirmText("");
    try { const { data } = await api.get(`/pages/${p.id}/impact`); setImpact(data); }
    catch (e) { setImpact({}); }
  };
  const doDelete = async () => {
    setDelBusy(true);
    try {
      await api.delete(`/pages/${delTarget.id}`);
      toast.success("Payment page moved to Bin");
      setDelTarget(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setDelBusy(false);
  };

  const pageUrl = (p) => `${BACKEND_URL}/r/${p.slug}`;
  const shortUrl = (p) => `${BACKEND_URL}/r/${p.short_code}`;

  const copy = (text, label = "Link copied") => { navigator.clipboard.writeText(text); toast.success(label); };

  const setStatus = async (p, status) => {
    try { await api.patch(`/pages/${p.id}/status?status=${status}`); toast.success(`Page ${status}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setMenuId(null);
  };
  const duplicate = async (p) => {
    try { await api.post(`/pages/${p.id}/duplicate`); toast.success("Page duplicated"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setMenuId(null);
  };

  return (
    <div onClick={() => setMenuId(null)}>
      <PageHeader title="Pages" subtitle="Create and manage registration / payment pages.">
        <button className={btnPrimary} onClick={() => navigate("/admin/pages/new")} data-testid="create-page-btn"><Plus className="w-4 h-4" /> Create New Page</button>
      </PageHeader>

      <Card>
        {pages === null ? <Loading /> : pages.length === 0 ? <Empty label="No pages yet — create your first registration page" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 font-semibold">Page</th>
                  <th className="px-5 py-3 font-semibold">School</th>
                  <th className="px-5 py-3 font-semibold">Program / Level / Batch</th>
                  <th className="px-5 py-3 font-semibold">Fee</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold text-right">Completed</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {pages.map((p) => (
                  <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid="page-row">
                    <td className="px-5 py-3.5">
                      <div className="font-semibold text-slate-800">{p.title}</div>
                      <div className="text-xs text-slate-400 font-mono">/r/{p.short_code}</div>
                    </td>
                    <td className="px-5 py-3.5 text-slate-600">{p.school_name || "—"}</td>
                    <td className="px-5 py-3.5 text-slate-600 text-xs">
                      {p.program_name || "—"}{p.level_name ? ` · ${p.level_name}` : ""}{p.batch_name ? ` · ${p.batch_name}` : ""}
                    </td>
                    <td className="px-5 py-3.5 font-medium text-slate-800">{inr(p.final_fee)}</td>
                    <td className="px-5 py-3.5"><StatusBadge status={p.status} />
                      {p.status === "closed" && p.auto_closed_at && <div className="text-[10px] text-rose-600 mt-1" data-testid="page-auto-full-tag">Batch full (auto)</div>}</td>
                    <td className="px-5 py-3.5 text-right" data-testid="page-completed-count">
                      <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold font-mono px-2.5 py-1 rounded-lg">{p.completed_count ?? 0}{p.seat_limit ? ` / ${p.seat_limit}` : ""}</span>
                      <div className="text-[10px] text-slate-400 mt-1">{p.seat_limit ? "seats filled" : "completed payments"}</div>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        <button title="Open" onClick={() => window.open(pageUrl(p), "_blank")} className="w-8 h-8 rounded-lg hover:bg-slate-100 text-slate-500 flex items-center justify-center" data-testid="page-open"><Eye className="w-4 h-4" /></button>
                        <button title="Edit" onClick={() => navigate(`/admin/pages/${p.id}/edit`)} className="w-8 h-8 rounded-lg hover:bg-blue-50 text-slate-500 hover:text-blue-600 flex items-center justify-center" data-testid="page-edit"><Pencil className="w-4 h-4" /></button>
                        <button title="Share" onClick={() => setSharePage(p)} className="w-8 h-8 rounded-lg hover:bg-slate-100 text-slate-500 flex items-center justify-center" data-testid="page-share"><Share2 className="w-4 h-4" /></button>
                        <div className="relative">
                          <button onClick={(e) => { e.stopPropagation(); setMenuId(menuId === p.id ? null : p.id); }} className="w-8 h-8 rounded-lg hover:bg-slate-100 text-slate-500 flex items-center justify-center" data-testid="page-menu"><MoreVertical className="w-4 h-4" /></button>
                          {menuId === p.id && (
                            <div className="absolute right-0 mt-1 w-44 bg-white rounded-xl border border-slate-200 shadow-xl z-20 py-1" onClick={(e) => e.stopPropagation()}>
                              <MenuItem icon={Copy} label="Duplicate" onClick={() => duplicate(p)} />
                              <MenuItem icon={Link2} label="Copy URL" onClick={() => { copy(pageUrl(p)); setMenuId(null); }} />
                              {p.status !== "published" && <MenuItem icon={Play} label="Publish" onClick={() => setStatus(p, "published")} />}
                              {p.status === "published" && <MenuItem icon={Pause} label="Pause" onClick={() => setStatus(p, "paused")} />}
                              <MenuItem icon={Archive} label="Archive" onClick={() => setStatus(p, "archived")} />
                              <div className="my-1 border-t border-slate-100" />
                              <MenuItem icon={Trash2} label="Delete" danger onClick={() => openDelete(p)} />
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={!!sharePage} onOpenChange={(o) => !o && setSharePage(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Share “{sharePage?.title}”</DialogTitle></DialogHeader>
          {sharePage && (
            <div className="space-y-4">
              <ShareRow label="Page URL" value={pageUrl(sharePage)} onCopy={() => copy(pageUrl(sharePage))} />
              <ShareRow label="Short URL" value={shortUrl(sharePage)} onCopy={() => copy(shortUrl(sharePage), "Short link copied")} />
              <div className="flex flex-wrap gap-2">
                <a href={`https://wa.me/?text=${encodeURIComponent("Register here: " + pageUrl(sharePage))}`} target="_blank" rel="noreferrer" className="flex-1 min-w-[120px] bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium py-2.5 rounded-xl flex items-center justify-center gap-2" data-testid="share-whatsapp"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
                <a href={`mailto:?subject=GDIOT PAY Registration&body=${encodeURIComponent(pageUrl(sharePage))}`} className="flex-1 min-w-[120px] bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm font-medium py-2.5 rounded-xl flex items-center justify-center gap-2"><Mail className="w-4 h-4" /> Email</a>
              </div>
              <div className="border border-slate-200 rounded-xl p-4 flex flex-col items-center gap-3">
                <img src={`${BACKEND_URL}/api/pages/${sharePage.id}/qr`} alt="QR" className="w-40 h-40" data-testid="page-qr-img" />
                <a href={`${BACKEND_URL}/api/pages/${sharePage.id}/qr`} download={`${sharePage.slug}.png`} className="text-blue-600 text-sm font-medium flex items-center gap-1.5 hover:underline"><QrCode className="w-4 h-4" /> Download QR Code</a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent data-testid="delete-page-dialog">
          <DialogHeader><DialogTitle>Delete Payment Page?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Are you sure you want to delete this payment page?</p>
          {impact === null ? (
            <div className="flex items-center gap-2 text-sm text-slate-400 py-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading impact…</div>
          ) : (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm space-y-1.5" data-testid="delete-impact">
              <div className="font-semibold text-slate-700 mb-1">This payment page currently has:</div>
              <Row l="Registrations" v={impact.registrations ?? 0} />
              <Row l="Completed Payments" v={impact.completed_payments ?? 0} />
              <Row l="Payment Transactions" v={impact.transactions ?? 0} />
              <Row l="Students" v={impact.students ?? 0} />
              <div className="flex justify-between border-t border-slate-200 pt-1.5 mt-1.5"><span className="font-semibold text-slate-700">Total Active Collection</span><span className="font-bold text-blue-600">{inr(impact.collection ?? 0)}</span></div>
            </div>
          )}
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2.5 text-xs">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>All payments and payment-related records received through this page will be removed from active data and will no longer be included in the main collection. The deleted page and its associated data will be kept in the Bin for 10 days before permanent deletion.</span>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Type the page name to confirm: <span className="text-slate-800">{delTarget?.title}</span></label>
            <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="delete-confirm-input"
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder={delTarget?.title} />
          </div>
          <DialogFooter>
            <button onClick={() => setDelTarget(null)} className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 font-medium">Cancel</button>
            <button onClick={doDelete} disabled={delBusy || confirmText.trim() !== (delTarget?.title || "").trim()} data-testid="confirm-delete-page-btn"
              className="px-4 py-2 rounded-lg bg-rose-600 text-white font-semibold flex items-center gap-2 disabled:opacity-50">{delBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Delete Payment Page</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, danger }) {
  return <button data-testid={`page-menu-${label.toLowerCase().replace(/\s+/g, "-")}`} onClick={onClick} className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-slate-50 ${danger ? "text-rose-600" : "text-slate-700"}`}><Icon className={`w-4 h-4 ${danger ? "text-rose-500" : "text-slate-400"}`} /> {label}</button>;
}
function Row({ l, v }) {
  return <div className="flex justify-between"><span className="text-slate-500">{l}</span><span className="font-medium text-slate-700">{v}</span></div>;
}
function ShareRow({ label, value, onCopy }) {
  return (
    <div>
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <input readOnly value={value} className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono text-slate-600" />
        <button onClick={onCopy} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5"><Copy className="w-4 h-4" /> Copy</button>
      </div>
    </div>
  );
}
